import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { events, horses, season2026 } from '../data/mock';
import { MapPin, Trophy, ArrowRight, Newspaper, CalendarDays, CalendarPlus, Download, ListOrdered } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { formatRange, downloadIcs } from '../utils/calendar';

const HERO_IMG = 'https://www.horseshowjumping.tv/wp-content/uploads/2026/06/BASSI-Giacomo-ITA-CAPE-COD_CSIO-Roma-2026_20260529_SS358075.jpg';
const TODAY = new Date().toISOString().slice(0, 10);

const rank = (placing) => parseInt(placing, 10);

const placingColor = (placing) => {
  const r = rank(placing);
  if (r === 1) return 'text-pink-400';
  if (r === 2 || r === 3) return 'text-cyan-400';
  return 'text-zinc-300';
};

const daysBetween = (a, b) => Math.abs((new Date(a) - new Date(b)) / 86400000);
const findResult = (show) => events.past.find((p) => daysBetween(p.startDate, show.startDate) <= 3);

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const AddToCalendarButton = ({ event, t, size = 'sm' }) => (
  <button
    type="button"
    data-testid={`add-to-calendar-${event.id}`}
    onClick={() => downloadIcs([event], `${slug(event.name)}-${event.startDate}.ics`)}
    className={`inline-flex items-center gap-2 border border-cyan-400/60 text-cyan-400 hover:bg-cyan-400 hover:text-black transition-colors tracking-wider font-medium ${size === 'sm' ? 'text-xs px-3 py-1.5' : 'text-sm px-5 py-2.5'}`}
  >
    <CalendarPlus size={14} /> {t('addToCalendar')}
  </button>
);

const ResultsList = ({ results, eventId }) => (
  <ul className="space-y-2 mb-4">
    {results.map((r, i) => {
      const horse = horses.find((h) => h.id === r.horseId);
      return (
        <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
          <Trophy size={14} className={placingColor(r.placing)} />
          <span className={`font-black ${placingColor(r.placing)}`}>{r.placing}</span>
          <span className="text-zinc-300">{r.competition}</span>
          {horse && (
            <Link
              to={`/horses/${horse.id}`}
              data-testid={`calendar-horse-link-${eventId}-${horse.id}`}
              className="text-zinc-500 hover:text-cyan-400 transition-colors underline-offset-4 hover:underline"
            >
              · {horse.name}
            </Link>
          )}
        </li>
      );
    })}
  </ul>
);

const EventCard = ({ event, mode, lang, t, onViewResults }) => {
  const start = new Date(event.startDate);
  const locale = lang === 'it' ? 'it-IT' : 'en-GB';
  const isPast = event.endDate < TODAY;
  const linked = mode === 'season' && isPast ? findResult(event) : null;
  const showResults = mode === 'past';
  const accent = showResults || linked ? 'border-pink-500' : isPast ? 'border-zinc-600' : 'border-cyan-400';
  const isStage = event.type === 'Stage';

  return (
    <div
      data-testid={`calendar-event-${mode}-${event.id}`}
      className={`group flex flex-col md:flex-row gap-6 bg-zinc-900/60 border border-zinc-800 border-l-4 ${accent} p-6 hover:bg-zinc-900 transition-colors ${isPast && mode === 'season' && !linked ? 'opacity-60' : ''}`}
    >
      <div className="md:w-28 flex-shrink-0 flex md:flex-col items-baseline md:items-start gap-2 md:gap-0">
        <span className="text-4xl font-black text-white leading-none">{start.getDate()}</span>
        <span className="text-sm uppercase tracking-widest text-zinc-400">
          {start.toLocaleDateString(locale, { month: 'short' }).replace('.', '')} {start.getFullYear()}
        </span>
        <span className={`text-xs mt-1 ${showResults || linked ? 'text-pink-400' : 'text-cyan-400'}`}>
          {formatRange(event.startDate, event.endDate, lang)}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <span className={`text-xs font-bold px-2 py-1 tracking-wider ${showResults || linked ? 'bg-pink-500/15 text-pink-400' : 'bg-cyan-400/15 text-cyan-400'}`}>
            {event.type}
          </span>
          <h3 className="text-xl font-bold text-white">{event.name}</h3>
          {mode === 'season' && isPast && (
            <span className="text-xs uppercase tracking-widest text-zinc-500">{t('completed')}</span>
          )}
        </div>
        <p className="flex items-center gap-2 text-zinc-400 text-sm mb-4">
          <MapPin size={14} /> {event.location}
        </p>

        {showResults && event.results && <ResultsList results={event.results} eventId={event.id} />}
        {linked && <p className="text-pink-400 text-sm font-medium mb-4">{linked.result}</p>}

        <div className="flex flex-wrap items-center gap-4">
          {showResults && event.newsId && (
            <Link
              to={`/news/${event.newsId}`}
              data-testid={`calendar-news-link-${event.id}`}
              className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-400 text-sm font-medium tracking-wider transition-colors"
            >
              <Newspaper size={14} /> {t('readNews')} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
          {linked && (
            <button
              type="button"
              data-testid={`calendar-view-results-${event.id}`}
              onClick={onViewResults}
              className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-400 text-sm font-medium tracking-wider transition-colors"
            >
              <Trophy size={14} /> {t('viewResults')} <ArrowRight size={14} />
            </button>
          )}
          {!isPast && !isStage && <AddToCalendarButton event={event} t={t} />}
        </div>
      </div>
    </div>
  );
};

const Calendar = () => {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState('upcoming');

  const upcoming = events.upcoming;
  const past = [...events.past].sort((a, b) => b.startDate.localeCompare(a.startDate));
  const season = past.filter((e) => e.startDate.startsWith('2026'));
  const allResults = season.flatMap((e) => e.results || []);
  const wins = allResults.filter((r) => rank(r.placing) === 1).length;
  const podiums = allResults.filter((r) => rank(r.placing) <= 3).length;

  const stats = [
    { key: 'wins', value: wins, label: t('winsLabel'), color: 'text-pink-400' },
    { key: 'podiums', value: podiums, label: t('podiumsLabel'), color: 'text-cyan-400' },
    { key: 'shows', value: season2026.filter((e) => e.type !== 'Stage').length, label: t('showsLabel'), color: 'text-white' },
    { key: 'upcoming', value: upcoming.length, label: t('upcomingLabel'), color: 'text-white' }
  ];

  const tabs = [
    { key: 'upcoming', label: t('upcomingShows'), icon: CalendarDays, active: 'bg-cyan-400 text-black' },
    { key: 'season', label: t('seasonTab'), icon: ListOrdered, active: 'bg-white text-black' },
    { key: 'past', label: t('pastShows'), icon: Trophy, active: 'bg-pink-500 text-white' }
  ];

  const list = tab === 'upcoming' ? upcoming : tab === 'season' ? season2026 : past;
  const icsList = tab === 'upcoming' ? upcoming : season2026.filter((e) => e.type !== 'Stage');

  return (
    <div className="min-h-screen bg-black" data-testid="calendar-page">
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center 30%', filter: 'grayscale(100%)' }} />
        <div className="absolute inset-0" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center 30%', maskImage: 'radial-gradient(ellipse 45% 55% at 50% 50%, black 20%, transparent 60%)', WebkitMaskImage: 'radial-gradient(ellipse 45% 55% at 50% 50%, black 20%, transparent 60%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,0.95) 100%)' }} />
        <div className="relative z-10 text-center text-white px-4 pt-16">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            {t('calendarTitle').split(' ')[0]} <span className="text-cyan-400">{t('calendarTitle').split(' ').slice(1).join(' ')}</span>
          </h1>
          <p className="text-xl md:text-2xl font-light text-zinc-300">{t('calendarSubtitle')}</p>
        </div>
      </section>

      <section className="py-10 bg-zinc-950 border-b border-zinc-800">
        <div className="container mx-auto px-4">
          <p className="text-center text-xs tracking-[0.3em] text-zinc-500 uppercase mb-6">{t('calendarStats')} · 2026</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {stats.map((s) => (
              <div key={s.key} data-testid={`calendar-stat-${s.key}`}>
                <p className={`text-4xl md:text-5xl font-black ${s.color}`}>{s.value}</p>
                <p className="text-zinc-400 uppercase tracking-wide text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 bg-zinc-900 border-b border-zinc-800">
        <div className="container mx-auto px-4 flex flex-wrap gap-3 justify-center">
          {tabs.map(({ key, label, icon: Icon, active }) => (
            <button
              key={key}
              data-testid={`calendar-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${tab === key ? active : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 max-w-5xl">
          {tab !== 'past' && (
            <div className="flex justify-end mb-6">
              <button
                type="button"
                data-testid="download-season-ics"
                onClick={() => downloadIcs(icsList, tab === 'upcoming' ? 'giacomo-bassi-prossimi-concorsi.ics' : 'giacomo-bassi-stagione-2026.ics')}
                className="inline-flex items-center gap-2 bg-cyan-400 text-black hover:bg-cyan-300 px-5 py-2.5 text-sm font-bold tracking-wider transition-colors"
              >
                <Download size={16} /> {tab === 'upcoming' ? t('downloadUpcoming') : t('downloadSeason')}
              </button>
            </div>
          )}
          <div className="space-y-4">
            {list.length === 0 && <p className="text-center text-zinc-500">{t('noUpcoming')}</p>}
            {list.map((event) => (
              <EventCard
                key={`${tab}-${event.id}`}
                event={event}
                mode={tab}
                lang={language}
                t={t}
                onViewResults={() => setTab('past')}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calendar;
